import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import PropTypes from "prop-types";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "../Context/AuthContext";
import { useAlertContext } from "../Context/AlertContext";
import { useSubscriptionAccess } from "../Context/SubscriptionAccessContext";
import LangSwitcher from "../Component/LangSwitcher";
import ErrorMessage from "../Component/ErrorMessage";
import SuccessMessage from "../Component/SuccessMessage";
import SubscribeRequiredModal from "../Pages/Subscription/SubscribeRequiredModal";
import { DASHBOARD_NAV_ITEMS } from "./dashboardNavItems";
import "./dashboard-layout.css";
import "./dashboard-content.css";

const DashboardShellContext = createContext({
  toggleSidebar: () => {}
});

export const useDashboardShell = () => useContext(DashboardShellContext);

const DashboardSidebar = ({ open, onClose }) => {
  const { t } = useTranslation("common");
  const location = useLocation();
  const { userAttributes, user, logout, submitting } = useAuthContext();
  const {
    checking,
    navigateWithSubscriptionCheck,
    isRestrictedMenuDisabled
  } = useSubscriptionAccess();
  const isBillingRoute =
    location.pathname === "/billing/invoices" || location.pathname.startsWith("/billing/");
  const [billingOpen, setBillingOpen] = useState(isBillingRoute);
  const [navigatingPath, setNavigatingPath] = useState(null);
  const billingGroupRef = useRef(null);

  useEffect(() => {
    if (isBillingRoute) {
      setBillingOpen(true);
    }
  }, [isBillingRoute]);

  useEffect(() => {
    if (!billingOpen) return undefined;
    const frame = requestAnimationFrame(() => {
      billingGroupRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    });
    return () => cancelAnimationFrame(frame);
  }, [billingOpen]);

  const email = useMemo(() => {
    if (user?.attributes?.email) return user.attributes.email;
    return userAttributes.reduce((prev, curr) => ({ ...prev, [curr.Name]: curr.Value }), {})?.email || "";
  }, [user, userAttributes]);

  const avatarLetter = (email?.[0] || "U").toUpperCase();

  const handleProtectedNav = async (event, path) => {
    event.preventDefault();
    if (navigatingPath || checking) return;
    if (isRestrictedMenuDisabled(path)) return;

    setNavigatingPath(path);
    try {
      await navigateWithSubscriptionCheck(path);
      onClose();
    } finally {
      setNavigatingPath(null);
    }
  };

  return (
    <aside className={`sidebar${open ? " open" : ""}`}>
      <div className="sidebar-logo">
        <Link to="/home" className="logo-mark" onClick={onClose}>
          <div className="logo-icon">⚡</div>
          <span className="logo-text">{process.env.REACT_APP_WESITE_NAME || "Odoo API"}</span>
        </Link>
      </div>

      <div className="sidebar-user">
        <div className="avatar">{avatarLetter}</div>
        <div className="user-info">
          <div className="user-email">{email}</div>
          <span className="user-role">{t("Pro Plan")}</span>
        </div>
      </div>

      <div className="sidebar-body">
      <nav className="sidebar-nav">
        <div className="nav-label-group">{t("Language")}</div>
        <div className="sidebar-lang">
          <LangSwitcher className="sidebar-lang-switcher" />
        </div>

        <div className="nav-label-group nav-label-spaced">{t("Navigation")}</div>
        {DASHBOARD_NAV_ITEMS.map((item) => {
          const isActive =
            location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
          const disabled = isRestrictedMenuDisabled(item.path);
          const busy = navigatingPath === item.path;

          if (disabled) {
            return (
              <span
                key={item.path}
                className={`nav-item nav-item-disabled${isActive ? " active" : ""}`}
                aria-disabled="true"
                title={t("Renew your subscription to access this feature.")}>
                <span className="nav-icon">{item.icon}</span>
                <span>{t(item.labelKey)}</span>
              </span>
            );
          }

          return (
            <a
              key={item.path}
              href={item.path}
              className={`nav-item${isActive ? " active" : ""}${busy ? " nav-item-busy" : ""}`}
              onClick={(event) => handleProtectedNav(event, item.path)}>
              <span className="nav-icon">{item.icon}</span>
              <span>{t(item.labelKey)}</span>
            </a>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        <a
          href="/help-center"
          className={`nav-item sidebar-account-link${
            location.pathname === "/help-center" ? " active" : ""
          }`}
          onClick={(event) => handleProtectedNav(event, "/help-center")}>
          <span className="nav-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </span>
          <span>{t("Help Center")}</span>
        </a>

        <div
          ref={billingGroupRef}
          className={`sidebar-nav-group${billingOpen || isBillingRoute ? " open" : ""}`}>
          <button
            type="button"
            className={`nav-item sidebar-account-link nav-group-toggle${
              isBillingRoute ? " active" : ""
            }`}
            onClick={() => setBillingOpen((value) => !value)}
            aria-expanded={billingOpen || isBillingRoute}>
            <span className="nav-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="5" width="20" height="14" rx="2" />
                <line x1="2" y1="10" x2="22" y2="10" />
              </svg>
            </span>
            <span className="nav-group-label">{t("Billing")}</span>
            <span className="nav-group-chevron" aria-hidden="true">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={
                  billingOpen || isBillingRoute ? "nav-group-chevron-icon open" : "nav-group-chevron-icon"
                }>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </span>
          </button>
          {billingOpen || isBillingRoute ? (
            <div className="nav-submenu">
              <a
                href="/billing/invoices"
                className={`nav-item nav-subitem${
                  location.pathname === "/billing/invoices" ? " active" : ""
                }`}
                onClick={(event) => handleProtectedNav(event, "/billing/invoices")}>
                <span className="nav-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="8" y1="13" x2="16" y2="13" />
                    <line x1="8" y1="17" x2="13" y2="17" />
                  </svg>
                </span>
                <span>{t("Invoice")}</span>
              </a>
              <a
                href="/billing/manage-credits"
                className={`nav-item nav-subitem${
                  location.pathname === "/billing/manage-credits" ? " active" : ""
                }`}
                onClick={(event) => handleProtectedNav(event, "/billing/manage-credits")}>
                <span className="nav-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v12M8 10h8M8 14h5" />
                  </svg>
                </span>
                <span>{t("Manage Credits")}</span>
              </a>
            </div>
          ) : null}
        </div>

        <a
          href="/profile"
          className={`nav-item sidebar-account-link${location.pathname === "/profile" ? " active" : ""}`}
          onClick={(event) => handleProtectedNav(event, "/profile")}>
          <span className="nav-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </span>
          <span>{t("My Account")}</span>
        </a>
        <button type="button" className="btn-logout" onClick={logout} disabled={submitting}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          <span>{t("Logout")}</span>
        </button>
      </div>
      </div>
    </aside>
  );
};

DashboardSidebar.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { errors, successMessage } = useAlertContext();

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useLayoutEffect(() => {
    document.body.classList.add("dashboard-active");
    return () => document.body.classList.remove("dashboard-active");
  }, []);

  const shellValue = useMemo(
    () => ({
      toggleSidebar: () => setSidebarOpen((value) => !value)
    }),
    []
  );

  return (
    <DashboardShellContext.Provider value={shellValue}>
      <div className="dashboard-shell">
        <div
          className={`sidebar-overlay${sidebarOpen ? " open" : ""}`}
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
        <DashboardSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="main">
          <ErrorMessage errors={errors} />
          <SuccessMessage message={successMessage} />
          {children}
          <SubscribeRequiredModal />
        </div>
      </div>
    </DashboardShellContext.Provider>
  );
};

DashboardLayout.propTypes = {
  children: PropTypes.node.isRequired
};

export default DashboardLayout;
